"use client";

import { useState, useMemo } from "react";
import Image from "next/image";
import { motion } from "framer-motion";
import { Camera, Images } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Header } from "@/components/shared/header";
import { Footer } from "@/components/shared/footer";
import { Lightbox } from "@/components/gallery/lightbox";
import { PROPERTY_SECTIONS, GALLERY_IMAGES } from "@/lib/constants";
import { cn } from "@/lib/utils";

export default function GalleryPage() {
  const [activeSection, setActiveSection] = useState<string | null>(null);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);

  const allImages = useMemo(
    () => Object.values(GALLERY_IMAGES).flat(),
    []
  );

  const filteredImages = activeSection
    ? GALLERY_IMAGES[activeSection] || []
    : allImages;

  const openLightbox = (index: number) => {
    setLightboxIndex(index);
    setLightboxOpen(true);
  };

  const lightboxImages = activeSection ? filteredImages : allImages;

  return (
    <>
      <Header />
      <main className="min-h-screen pt-24 pb-16">
        <div className="mx-auto max-w-7xl px-4 md:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center"
          >
            <p className="text-sm font-medium uppercase tracking-[0.2em] text-primary">
              Gallery
            </p>
            <h1 className="mt-3 font-playfair text-3xl font-bold text-foreground md:text-4xl lg:text-5xl">
              Explore {PROPERTY_SECTIONS.length} Stunning Spaces
            </h1>
            <p className="mx-auto mt-4 max-w-2xl text-muted-foreground">
              Take a visual tour of every corner of our luxury Balinese villa &mdash;
              from the infinity pool to the sky deck, each space is crafted for
              unforgettable experiences.
            </p>
          </motion.div>

          {/* Section Filters */}
          <div className="mt-10 flex flex-wrap items-center justify-center gap-2">
            <Button
              variant={activeSection === null ? "default" : "outline"}
              size="sm"
              onClick={() => setActiveSection(null)}
              className={cn(
                "rounded-full",
                activeSection === null
                  ? "bg-primary text-primary-foreground"
                  : "border-border text-muted-foreground hover:text-foreground hover:border-primary/50"
              )}
            >
              <Images className="size-3.5" />
              All ({allImages.length})
            </Button>
            {PROPERTY_SECTIONS.map((section) => {
              const count = (GALLERY_IMAGES[section.id] || []).length;
              return (
                <Button
                  key={section.id}
                  variant={activeSection === section.id ? "default" : "outline"}
                  size="sm"
                  onClick={() => setActiveSection(section.id)}
                  className={cn(
                    "rounded-full",
                    activeSection === section.id
                      ? "bg-primary text-primary-foreground"
                      : "border-border text-muted-foreground hover:text-foreground hover:border-primary/50"
                  )}
                >
                  {section.label} ({count})
                </Button>
              );
            })}
          </div>

          {/* Photo Grid */}
          <div className="mt-10">
            {activeSection === null ? (
              PROPERTY_SECTIONS.map((section) => {
                const images = GALLERY_IMAGES[section.id] || [];
                if (images.length === 0) return null;

                let globalStartIndex = 0;
                for (const s of PROPERTY_SECTIONS) {
                  if (s.id === section.id) break;
                  globalStartIndex += (GALLERY_IMAGES[s.id] || []).length;
                }

                return (
                  <div key={section.id} className="mb-12">
                    <motion.h2
                      initial={{ opacity: 0, x: -10 }}
                      whileInView={{ opacity: 1, x: 0 }}
                      viewport={{ once: true }}
                      className="mb-5 font-playfair text-xl font-semibold text-foreground md:text-2xl"
                    >
                      {section.label}
                    </motion.h2>
                    <div className="grid grid-cols-2 gap-3 md:grid-cols-3 lg:grid-cols-4 md:gap-4">
                      {images.map((img, i) => {
                        const isLarge = i === 0 && images.length > 2;
                        return (
                          <motion.div
                            key={img.src}
                            initial={{ opacity: 0, y: 20 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.4, delay: (i % 8) * 0.05 }}
                            className={cn(
                              "group relative cursor-pointer overflow-hidden rounded-xl",
                              isLarge
                                ? "col-span-2 row-span-2 aspect-[4/3]"
                                : "aspect-square"
                            )}
                            onClick={() =>
                              openLightbox(globalStartIndex + i)
                            }
                          >
                            <Image
                              src={img.src}
                              alt={img.alt}
                              fill
                              loading="lazy"
                              className="object-cover transition-transform duration-500 group-hover:scale-110"
                              sizes={
                                isLarge
                                  ? "(max-width: 768px) 100vw, 50vw"
                                  : "(max-width: 768px) 50vw, 25vw"
                              }
                            />
                            <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent opacity-0 transition-opacity duration-300 group-hover:opacity-100" />
                            <div className="absolute inset-0 flex items-center justify-center opacity-0 transition-opacity duration-300 group-hover:opacity-100">
                              <Camera className="size-8 text-white drop-shadow-lg" />
                            </div>
                            <p className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/60 to-transparent px-3 pb-2 pt-6 text-xs text-white/80 opacity-0 transition-opacity duration-300 group-hover:opacity-100">
                              {img.alt}
                            </p>
                          </motion.div>
                        );
                      })}
                    </div>
                  </div>
                );
              })
            ) : (
              <div className="grid grid-cols-2 gap-3 md:grid-cols-3 lg:grid-cols-4 md:gap-4">
                {filteredImages.map((img, i) => {
                  const isLarge = i === 0 && filteredImages.length > 2;
                  return (
                    <motion.div
                      key={img.src}
                      initial={{ opacity: 0, y: 20 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true }}
                      transition={{ duration: 0.4, delay: (i % 8) * 0.05 }}
                      className={cn(
                        "group relative cursor-pointer overflow-hidden rounded-xl",
                        isLarge
                          ? "col-span-2 row-span-2 aspect-[4/3]"
                          : "aspect-square"
                      )}
                      onClick={() => openLightbox(i)}
                    >
                      <Image
                        src={img.src}
                        alt={img.alt}
                        fill
                        loading="lazy"
                        className="object-cover transition-transform duration-500 group-hover:scale-110"
                        sizes={
                          isLarge
                            ? "(max-width: 768px) 100vw, 50vw"
                            : "(max-width: 768px) 50vw, 25vw"
                        }
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent opacity-0 transition-opacity duration-300 group-hover:opacity-100" />
                      <div className="absolute inset-0 flex items-center justify-center opacity-0 transition-opacity duration-300 group-hover:opacity-100">
                        <Camera className="size-8 text-white drop-shadow-lg" />
                      </div>
                      <p className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/60 to-transparent px-3 pb-2 pt-6 text-xs text-white/80 opacity-0 transition-opacity duration-300 group-hover:opacity-100">
                        {img.alt}
                      </p>
                    </motion.div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </main>
      <Footer />

      {lightboxOpen && (
        <Lightbox
          images={lightboxImages}
          currentIndex={lightboxIndex}
          onClose={() => setLightboxOpen(false)}
          onNavigate={setLightboxIndex}
        />
      )}
    </>
  );
}
